import { useCallback, useEffect, useMemo, useState } from 'react';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import { PortfolioHistoryExportTypeFilter } from 'src/components/PortfolioPage/PortfolioHistorySubPage/PortfolioHistoryExportsTable/types';
import { DataTablePagination } from 'src/components/primitives/Table/types';

interface Params {
	exportTypeFilter: PortfolioHistoryExportTypeFilter;
}

const PAGE_SIZE = 20;

export default function useExportsTable({ exportTypeFilter }: Params) {
	const { initialHistoryLoaded, records, counts } = useDriftAccountStore(
		(s) => s.downloadFiles
	);

	const accounts = useLazySubAccounts();
	const deletedAccounts = useDriftAccountStore((s) => s.deletedAccounts);

	const allRecords = useMemo(
		() =>
			records.pending
				.concat(records.available)
				.sort((a, b) => b.lastModifiedTs - a.lastModifiedTs),
		[records]
	);
	const totalCount = useMemo(() => counts.pending + counts.available, [counts]);

	const filteredRecords = useMemo(() => {
		if (exportTypeFilter === 'all') {
			return allRecords;
		}

		return allRecords.filter(
			(record) => record.requestParams.fileType === exportTypeFilter
		);
	}, [allRecords, exportTypeFilter]);

	const [visibleRecords, setVisibleRecords] = useState(
		filteredRecords.slice(0, PAGE_SIZE)
	);

	useEffect(() => {
		setVisibleRecords(filteredRecords.slice(0, PAGE_SIZE));
	}, [filteredRecords]);

	const [pageIndex, setPageIndex] = useState(0);

	const onPageChange = useCallback(
		(newPage: number) => {
			if (newPage === pageIndex) {
				return;
			}

			setVisibleRecords(
				filteredRecords.slice(
					newPage * PAGE_SIZE,
					newPage * PAGE_SIZE + PAGE_SIZE
				)
			);

			setPageIndex(newPage);
		},
		[pageIndex]
	);

	const pagination = useMemo<DataTablePagination>(
		() => ({
			onPageChange,
			pageIndex,
			pageSize: PAGE_SIZE,
			totalCount,
			message: 'Downloads for prepared statements will expire after 1 month.',
		}),
		[onPageChange, pageIndex, totalCount]
	);

	return {
		data: visibleRecords,
		pagination,
		accounts,
		deletedAccounts,
		isLoading: !initialHistoryLoaded,
	};
}
